<template>
  <slot 
    name="list-actions"
    :state="state"
    :pagination="pagination"
    :selectAll="selectAll"
  ></slot>

  <slot 
    name="list-header"
    :state="state"
    :pagination="pagination"
    :selectAll="selectAll"
  ></slot>

  <ul 
    class="f-list--results"
  >
    <template v-if="showItems">
      <li
        v-for="item in items"
        :key="item[idKey]"
      >
        <slot 
          name="list-item"
          :item="item"
          :state="state"
          :toggleSelection="toggleSelection"
        ></slot>
      </li> 
    </template>

    <template v-if="showSkeleton"> 
      <li
        v-for="numero in pagination.limit - items.length"
        :key="`${numero}-${pagination.next}`"
      >
        <slot 
          name="list-skeleton"
          :state="state"
        >
          <f-box 
            class="f-list--skeleton margin-bottom-3"
            grid
          >
            <div class="is-col-10 is-grid is-aligned m-left-10">
              <f-box 
                class="is-col-1"
                type="info"
                disabled
                fill
                square
              >
                <f-loader loading></f-loader>
              </f-box>
              
              <p class="p-left-5">
                <small>
                  Chargement...
                </small>
              </p>
            </div>
          </f-box>
        </slot>
      </li> 
    </template>  

    <template v-if="isEmpty">
      <f-box 
        class="f-list--empty"
      >
        <slot 
          name="empty"
          :state="state"
        >
          <p>
            <b>Aucun résultat</b> 
          </p>

          <h1>🧐</h1>
        </slot>
      </f-box>
    </template>
  </ul>

  <div class="f-list--pagination">
    <slot 
      name="pagination-count"
      :pagination="pagination"
      :limit-choices="limitChoices"
    >
      <f-select-text
        class="m-right-20"
        v-if="!pagination.loadMore"
        v-model.number="pagination.limit"
        :options="limitChoices"
        direction="up"
      >
        <template #prepend>
          <small>
            Afficher
          </small>
        </template>

        <template #label="{ label }">
          <small>
            {{ label }}
          </small>
        </template>
        
        <template #append>
          <small>
            résultats
          </small>
        </template>
      </f-select-text>
    </slot>

    <slot 
      name="pagination-controls"
      :loadPrevious="loadPrevious"
      :loadNext="loadNext"
      :pagination="pagination"
    >
      <f-button 
        v-if="!pagination.loadMore"
        @click="loadPrevious"
        :disabled="!pagination.hasPrevious"
        circle
      >
        <i class="uil uil-angle-left"></i>
      </f-button>

      <f-button 
        v-if="!pagination.loadMore"
        @click="loadNext"
        :disabled="!pagination.hasNext"
        circle
      >
        <i class="uil uil-angle-right"></i>
      </f-button>

      <div  
        v-if="pagination.loadMore"
        class="is-full-width is-centered"
      >
        <f-button 
          @click="loadNext"
          :disabled="loading || !pagination.hasNext"
          circle
        >
          <f-loader
            v-if="loading"
            loading
          ></f-loader>

          <i 
            v-if="!loading"
            class="uil uil-plus"
          ></i>
        </f-button>
      </div>
    </slot>

    <slot 
      name="pagination-custom"
      :pagination="pagination"
      :limit-choices="limitChoices"
    ></slot>
  </div>

  <span></span>
</template>

<script>
  import { computed, reactive, ref, toRefs, watch } from 'vue'
  import useList from '@composables/use-list'

  export default {
    props: {
      store: {
        type: String, 
        required: true,
      },

      query: {
        type: Object,
        default: () => {},
      },

      search: {
        type: String,
        default: '',
      },

      fuzzy: {
        type: Object,
        default: () => {},
      },

      paginate: {
        type: Boolean,
        default: true,
      },

      skip: {
        type: Number, 
        default: 0,
      },

      limit: {
        type: Number, 
        default: 10,
      },

      limitChoices: {
        type: Array,
        default: () => [5, 10, 15, 20]
      },

      loadMore: {
        type: Boolean,
        default: false,
      },

      forceEmpty: {
        type: Boolean,
        default: false,
      },

      multiple: {
        type: Boolean,
        default: true,
      },

      selection: {
        type: Array,
        default: () => [],
      },
    },

    setup (props) {
      const state = reactive({
        ...useList(props),
        info: computed(() => {
          return state.loading && !state.items.length ? 'Chargement...' : ''
        }),
        showSkeleton: computed(() =>
          !props.forceEmpty
          && state.loading 
          && !state.searching 
          && state.items.length < state.pagination.limit
        ),
        showItems: computed(() => !props.forceEmpty && state.items.length),
        isEmpty: computed(() => 
          props.forceEmpty || (!state.loading && !state.items.length)
        )
      })

      return toRefs(state)
    }
  }
</script>

<style lang="scss">
  .f-list--results {
    position: relative;
    height: 100%;
    width: 100%;
    min-height: 500px;
    max-height: 500px;
    overflow: auto;
    overflow-x: hidden;
  }

  .f-list--empty {
    width: 100%;
    height: 500px;
    max-height: 100%;
  }

  .f-list--pagination {
    display: flex;
    align-items: center;
    width: 100%;
    margin-top: 0.5rem;
  }

  .f-list--skeleton {
    padding: 0.352rem 0 0.352rem 0.5rem;
  }
</style>